import * as React from 'react';

import { useCan } from '@/components/can/Can';
import { DataTable } from '@/components/dataTable';
import { useDataTable } from '@/components/dataTable/hooks/use-data-table';
import { DataTableToolbar } from '@/components/dataTable/ToolBar';
import { useConfirmationStore } from '@/components/dialog/confirmation.store';
import { PERMS } from '@/features/auth/permissions/permissionKeys';
import { useDynamicLocale } from '@/hoslog-intl';
import { Pagination } from '@/react-query/context/ApiClientContextController';
import { useMutation } from '@/react-query/hooks/useMutation/useMutation';
import { PremiumStores } from '@/services/premiumStores/premiumStores.types';
import { revalidate } from '@/services/revalidate/revalidate';
import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { PremiumStoresActionBar } from './ActionBar';
import { getColumns } from './Column';

export function PendingStoreTable({
  data,
  isLoading,
  pagination,
}: {
  data: PremiumStores[];
  isLoading: boolean;
  pagination: Pagination;
}) {
  const queryClient = useQueryClient();
  const { openConfirmation } = useConfirmationStore();
  const { t } = useDynamicLocale();
  const canApproveClub = useCan({ allOf: [PERMS.club.premium.approve] });
  const canRejectClub = useCan({ allOf: [PERMS.club.premium.reject] });

  const { mutateAsync: approveStoreInfo } = useMutation('approvePremiumStoreInfo', {
    onSuccess: async (data) => {
      await queryClient.invalidateQueries({ queryKey: ['P', 'premiumStores', 'list'], exact: false });
      toast.success(data.message);
      await revalidate(['hostDetail', 'clubHosts', 'clubBasicInfo', 'all', 'editorRecommendedStores']);
    },
    onError: (err) => {
      toast.error(err.message);
    },
  });
  const { mutateAsync: rejectStoreInfo } = useMutation('rejectPremiumStoreInfo', {
    onSuccess: async (res) => {
      toast.success(res.message);
      await queryClient.invalidateQueries({ queryKey: ['P', 'premiumStores', 'list'], exact: false });
    },
    onError: (err) => {
      toast.error(err.message);
    },
  });
  const columns = React.useMemo(
    () => getColumns(approveStoreInfo, rejectStoreInfo, openConfirmation, t, canApproveClub, canRejectClub),
    [approveStoreInfo, rejectStoreInfo, openConfirmation, t, canApproveClub, canRejectClub],
  );

  const pageCount = React.useMemo(
    () => Math.ceil((pagination.totalItems ?? 0) / (pagination.pageSize ?? 1)),
    [pagination.totalItems, pagination.pageSize],
  );

  const { table } = useDataTable({
    data,
    columns,
    pageCount: pageCount,
    initialState: {
      sorting: [{ id: 'id', desc: true }],
    },
    meta: {
      title: t('storeManagement.premiumStores.table.title.pendingStores'),
      loading: isLoading,
      totalPage: pagination.totalItems,
    },
    getRowId: (originalRow) => String(originalRow.id),
    shallow: false,
    clearOnDefault: true,
  });

  return (
    <>
      <DataTable table={table} className="rounded-t-none">
        <DataTableToolbar />
        <PremiumStoresActionBar />
      </DataTable>
    </>
  );
}
